import { Loader2, Plus, CalendarX2, X, Calendar, CalendarDays } from 'lucide-react'
import { MdPodcasts } from 'react-icons/md'
import { useEffect, useRef, useState } from 'react'

import { FabComponent } from '@/modules/shared/components/fab.component'
import { BottomMenuComponent } from '@/modules/shared/components/bottom-menu.component'

import { PromotionCardComponent } from '../components/promotion-card.component'
import { PromotionSearchInputComponent } from '../components/promotion-search-input.component'
import { MENU_ACTIONS } from '../constants/menu-actions.constant'
import { usePromotionSearch } from '../hooks/use-promotion-search.hook'
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from '@/modules/shared/components/ui/alert-dialog'

export type PromotionSearchInterface = ReturnType<typeof usePromotionSearch>

export function PromotionSearchInterface({
	handleSearchPromotion,
	promotions,
	promotionSelected,
	selectedPromotions,
	isMultiSelectMode,
	handleSelectPromotionToStartActions,
	handleLongPressPromotion,
	handleExitMultiSelectMode,
	isDisabledDelete,
	handleStartViewPromotion,
	isDisabledEdit,
	handleStartAddPromotion,
	selectFunctionToActions,
	isLoading,
	isLoadingMore,
	hasMore,
	totalRecords,
	loadMorePromotions,
	canTransmit,
	loadInitialPromotions,
}: PromotionSearchInterface) {

	const loadingRef = useRef<HTMLDivElement>(null)
	const [showTodayOrders, setShowTodayOrders] = useState(false)

	// Intersection Observer para scroll infinito
	useEffect(() => {
		if (!hasMore || isLoadingMore || !promotions || promotions.length === 0 || !loadMorePromotions) return

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
					loadMorePromotions()
				}
			},
			{ threshold: 0.1 }
		)

		if (loadingRef.current) {
			observer.observe(loadingRef.current)
		}

		return () => observer.disconnect()
	}, [hasMore, isLoadingMore, loadMorePromotions, promotions])

	// Filtros rápidos
	const handleTodayFilter = () => {
		setShowTodayOrders(true)
		const today = new Date().toISOString().split('T')[0]
		handleSearchPromotion({
			filter: 'data',
			inputValue: today,
			filterType: 'igual'
		})
	}

	const handleLastOrdersFilter = () => {
		setShowTodayOrders(false)
		loadInitialPromotions()
	}

	const renderFabActions = () => {
		if (isMultiSelectMode) {
			return (
				<>
					<FabComponent
						bottomMenuOffset={true}
						className='bottom-20 lg:bottom-4'
						items={[
							{
								id: 'transmit',
								icon: MdPodcasts,
								type: 'transmit',
								disabled: selectedPromotions.length === 0,
								label: 'Transmitir',
							},
						]}
						action={selectFunctionToActions}
					/>
				</>
			)
		}

		if (promotionSelected) {
			const canTransmitSelected = !promotionSelected.dataInclusaoServidor ||
				promotionSelected.dataInclusaoServidor.toString().includes('1899');

			const menuItems = [
				...MENU_ACTIONS.map((item) => ({
					id: item.id,
					icon: item.icon,
					type: item.type,
					label: item.label,
					disabled:
						(item.type === 'delete' && isDisabledDelete) ||
						(item.type === 'startEdit' && isDisabledEdit),
				}))
			];

			if (canTransmitSelected) {
				menuItems.push({
					id: 'transmit',
					icon: MdPodcasts,
					type: 'transmit',
					label: 'Transmitir',
					disabled: false,
				});
			}

			return (
				<FabComponent
					bottomMenuOffset={true}
					className='bottom-20 lg:bottom-4'
					items={menuItems}
					action={selectFunctionToActions}
				/>
			)
		}

		return (
			<FabComponent
				className='bottom-20 lg:bottom-4'
				bottomMenuOffset={true}
				items={[
					{
						id: '1',
						icon: Plus,
						type: 'add',
						disabled: false,
						label: 'Adicionar',
					},
				]}
				action={handleStartAddPromotion}
			/>
		)
	}

	const getCounterText = () => {
		if (!promotions || promotions.length === 0) return ''

		if (totalRecords > 0) {
			const currentCount = promotions.length
			const isComplete = !hasMore

			if (isComplete) {
				return totalRecords === 1
					? `${totalRecords} visita encontrada`
					: `${totalRecords} visitas encontradas`
			} else {
				return `${currentCount} de ${totalRecords} visitas`
			}
		}

		return promotions.length === 1
			? `${promotions.length} visita encontrada`
			: `${promotions.length} visitas encontradas`
	}

	return (
		<div className="mb-2 min-h-screen">
			<PromotionSearchInputComponent onSubmitRequest={handleSearchPromotion} />

			<div className="flex   mt-2 mb-2">
				<div className="inline-flex rounded-lg border border-gray-200 p-1 bg-white shadow-xs">
					<div
						onClick={handleTodayFilter}
						className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 cursor-pointer ${showTodayOrders
							? 'bg-gray-200 text-gray-500'
							: 'bg-transparent text-gray-500 hover:bg-gray-50'
							}`}
					>
						<Calendar className="w-4 h-4" />
						Hoje
					</div>
					<div
						onClick={handleLastOrdersFilter}
						className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 cursor-pointer ${!showTodayOrders
							? 'bg-gray-200 text-gray-500'
							: 'bg-transparent text-gray-500 hover:bg-gray-50'
							}`}
					>
						<CalendarDays className="w-4 h-4" />
						Últimas visitas
					</div>
				</div>
			</div>

			<AlertDialog open={isLoading}>
				<AlertDialogContent className="flex flex-col items-center flex-1 top-[95%] m-0 pb-28 md:pb-40 md:pt-10 lg:p-8 w-screen max-w-full lg:max-w-lg  mb-2 lg:top-[50%]  rounded-t-2xl">
					<Loader2 className="animate-spin text-primary size-10  text-center" />
					<AlertDialogTitle className='hidden'>Carregando Visitas</AlertDialogTitle>
					<AlertDialogDescription className='text-center mb-4'>Aguarde enquanto as visitas estão sendo carregadas...</AlertDialogDescription>
				</AlertDialogContent>
			</AlertDialog>

			{!isLoading && !promotions && (
				<div className="w-full flex items-center justify-center">
					<p className="text-center mt-36">
						Lista de visitas está vazia, realize uma pesquisa.
					</p>
				</div>
			)}

			{!isLoading && promotions && promotions.length === 0 && (
				<div className="w-full flex flex-col items-center justify-center mt-20">
					<CalendarX2 className="size-16 text-gray-400 mb-4" />
					<h3 className="text-lg font-semibold text-gray-600 mb-2">
						Nenhuma visita encontrada
					</h3>
					<p className="text-center text-gray-500 px-4 mb-6">
						Não há visitas para os critérios de busca selecionados.
						Tente ajustar os filtros ou adicionar uma nova visita.
					</p>
				</div>
			)}

			{!isLoading && promotions && promotions.length > 0 && (
				<>
					<div className="flex items-center justify-between mt-4 mb-3 px-2">
						<p className="text-sm text-gray-600">
							{getCounterText()}
						</p>
					</div>
					<div className="grid md:grid-cols-3 grid-cols-1 gap-2 h-full">
						{promotions.map((entity, index) => (
							<PromotionCardComponent
								key={`${entity?.idVisita}-${index}`}
								promotionSelected={promotionSelected}
								selectedPromotions={selectedPromotions}
								isMultiSelectMode={isMultiSelectMode}
								entity={entity}
								onSelectEntity={handleSelectPromotionToStartActions}
								onLongPress={handleLongPressPromotion}
								handleStartView={handleStartViewPromotion}
							/>
						))}
					</div>

					{/* Scroll infinito */}
					{hasMore && (
						<div
							ref={loadingRef}
							className="flex items-center justify-center py-8"
						>
							{isLoadingMore ? (
								<div className="flex items-center gap-2">
									<Loader2 size={20} className="animate-spin text-primary" />
									<span className="text-sm text-muted-foreground">
										Carregando mais visitas...
									</span>
								</div>
							) : (
								<div className="text-sm text-gray-400">
									Role para baixo para carregar mais
								</div>
							)}
						</div>
					)}

					{/* Indicador de fim da lista */}
					{!hasMore && totalRecords > 0 && promotions.length >= 10 && (
						<div className="flex items-center justify-center py-8 border-t border-gray-200">
							<div className="text-center">
								<div className="text-sm text-gray-500 mb-1">
									✅ Todas as visitas foram carregadas
								</div>
								<div className="text-xs text-gray-400">
									{totalRecords} {totalRecords === 1 ? 'visita' : 'visitas'} no total
								</div>
							</div>
						</div>
					)}
				</>
			)}

			{renderFabActions()}

			<BottomMenuComponent />
		</div>
	)
}